import { Link } from "react-router-dom";
import {
  bondFlowOf,
  type BondListDecision,
  type BondSubmissionListItem,
} from "@/lib/brokerBonds";
import {
  bondStatusBadgeClass,
  bondStatusLabel,
  formatListDate,
  statusBadgeClass,
} from "@/lib/listFormat";
import { formatBondDate } from "@/lib/bondValueDisplay";

// BRK-S139 — the bond-order table, extracted.
//
// `BondSubmissionsPage` has owned it since BRK-S088; the account profile's
// Submissions tab is the second surface listing the same rows, and the
// Construction Bonds landing (BRK-S174) is the third. A repeated table is a
// repeated set of decisions — which columns, how a date reads, how a status is
// badged, and that a REFERRAL is its own chip rather than a replacement status.
// Copying it would have been copying all four.
//
// The SB number is always a LINK to `/surety/submissions/{id}`, drafts included:
// the pointer exists from draft start, so a half-filled order is listed exactly
// like a filed one and the number is the way back into it.

/** How a decision reads. Three words, and nothing behind them: the vocabulary
 *  is derived upstream from the broker-safe facts the order view already
 *  publishes, and there is no reason, score or tier to render (PMS-S461). */
const DECISION_LABEL: Record<BondListDecision, string> = {
  approved: "Approved",
  referred: "Referred",
  pending: "Pending",
};

export function BondOrdersTable({
  rows,
}: {
  rows: readonly BondSubmissionListItem[];
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-brk-border bg-brk-panel">
      {/* The table keeps its OWN scroller, so a wide row never pushes the page
          sideways at 375px. */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[60rem] text-left text-sm">
          <thead className="border-b border-brk-border text-xs font-semibold uppercase tracking-wide text-brk-dim">
            <tr>
              <th className="px-4 py-3">Submission</th>
              <th className="px-4 py-3">Insured</th>
              <th className="px-4 py-3">Bond type</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3">Bid date</th>
              <th className="px-4 py-3">Decision</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr
                key={b.id}
                className="border-t border-brk-border transition-colors hover:bg-brk-bg/60"
              >
                <td className="px-4 py-3">
                  <Link
                    to={`/surety/submissions/${b.id}`}
                    className="font-mono font-semibold text-brk-accent hover:underline"
                  >
                    {b.number}
                  </Link>
                </td>
                <td className="px-4 py-3 font-medium text-brk-text">
                  {b.accountLegalName ?? "—"}
                </td>
                <td className="px-4 py-3 text-brk-dim">
                  {b.bondSubproductName ?? b.bondProductName ?? "—"}
                </td>
                <td className="px-4 py-3 text-brk-dim">
                  {formatListDate(b.createdAt)}
                </td>
                {/* The BID date, not a created date: `m/d/yyyy` (D-10 rule 2),
                    and blank on a draft, which has no filed bid yet. A dash is
                    right here where it would be wrong on the account profile —
                    this is a table column beside six others, not a fact a
                    broker reads down the phone. */}
                <td className="px-4 py-3 text-brk-dim">
                  {b.bidDate ? formatBondDate(b.bidDate) : "—"}
                </td>
                <td className="px-4 py-3">
                  {/* NULL IS ITS OWN STATE, and not `pending`: a draft has had
                      no decision, where `pending` means one has not landed yet.
                      Showing them alike would tell a broker who has not
                      finished filling a request in that somebody is looking at
                      it. */}
                  {b.decision ? (
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(
                        b.decision,
                      )}`}
                    >
                      {DECISION_LABEL[b.decision]}
                    </span>
                  ) : (
                    <span className="text-brk-dim">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="flex flex-wrap items-center gap-1.5">
                    {/* A submitted STANDALONE pre-approval reads "Complete",
                        never "Submitted": its decision has landed and there is
                        nothing to wait for (BRK-S160). */}
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${bondStatusBadgeClass(
                        b.status,
                        bondFlowOf(b),
                      )}`}
                    >
                      {bondStatusLabel(b.status, bondFlowOf(b))}
                    </span>
                    {/* Referral is orthogonal to status — a draft can be
                        referred and a filed order can not be — so it reads as
                        its own chip rather than a replacement status. It stays
                        beside the new Decision column rather than being folded
                        into it: `decision` is where the REQUEST stands, and
                        this chip is why. */}
                    {b.referred && (
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(
                          "referred",
                        )}`}
                      >
                        Referred
                      </span>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
